/**
 * Where the API lives, resolved once from build-time configuration — requirement 4.4.
 *
 * The rule the whole task turns on: **no hostname literal in source.** The current site carries five
 * copies of one API hostname, so moving the API means finding all five, and a stale one fails only in
 * a browser. Here the value arrives as a `VITE_*` variable and this function is the only thing that
 * interprets it.
 *
 * It follows `@apuestapr/env`'s philosophy rather than its implementation, because a static build has
 * no process environment to validate: shape over presence, fail where the mistake was made, and name
 * the variable in the message. A base URL that is present but carries no scheme would otherwise
 * resolve as a *relative path* against the page's origin and 404 every request with nothing naming
 * the cause.
 *
 * **This file is byte-identical in `apps/web` and `apps/dashboard`, and a test asserts that.** The
 * task calls for a client in each app, and an app cannot import from another app; when a third
 * consumer appears the shared home is a package, not a relative path. Until then
 * `packages/config/src/frontends.test.ts` fails the moment the two copies diverge, so the duplication
 * cannot quietly become drift — which is what happened to the 47-versus-49 primitives this programme
 * is replacing.
 */

export interface ApiBaseUrlInput {
  /** The variable the value came from, so a failure names what to fix. */
  readonly variableName: string;
  readonly value: string | undefined;
  /**
   * `import.meta.env.DEV`. Absent configuration is the normal case in development — the Vite proxy
   * forwards `/api` to the local API, so an empty base URL is what keeps requests same-origin — and
   * a defect in a production build, where there is no proxy to fall back on.
   */
  readonly isDevelopment: boolean;
}

const guidance =
  'Set it to the API origin, scheme included and nothing after the host (for example the deployed api hostname). Leave it unset in development: the Vite proxy forwards /api to the local API, so requests stay same-origin.';

/**
 * The origin every request is prefixed with. `''` means same-origin, which is the development
 * arrangement and also a perfectly good production one if the API is ever put behind the same host.
 */
export const resolveApiBaseUrl = ({
  variableName,
  value,
  isDevelopment,
}: ApiBaseUrlInput): string => {
  const declared = value?.trim() ?? '';

  if (declared === '') {
    if (isDevelopment) return '';
    throw new Error(`${variableName} is required in a production build and was empty. ${guidance}`);
  }

  let parsed: URL;
  try {
    parsed = new URL(declared);
  } catch {
    throw new Error(`${variableName} must be an absolute URL and is "${declared}". ${guidance}`);
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error(
      `${variableName} must use http or https and uses "${parsed.protocol}". ${guidance}`,
    );
  }

  if (parsed.username !== '' || parsed.password !== '') {
    throw new Error(`${variableName} must not contain credentials. ${guidance}`);
  }

  // A path, query, or fragment here would be silently concatenated in front of every route and
  // produce a 404 per request. Rejecting it costs one comparison and removes a whole afternoon.
  if (parsed.pathname !== '/' || parsed.search !== '' || parsed.hash !== '') {
    throw new Error(
      `${variableName} must be an origin with no path, query, or fragment, and is "${declared}". ${guidance}`,
    );
  }

  return parsed.origin;
};
